import { Injectable, inject } from '@angular/core';
import {
  CanActivate,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService, UserRole } from '../services/auth.service';
import { environment } from '../../../environments/environment';

/**
 * Route Guard for authentication and authorization
 * 
 * Can be disabled in development by setting environment.disableAuthGuards = true
 * 
 * Usage in routes:
 * ```typescript
 * {
 *   path: 'admin',
 *   component: AdminComponent,
 *   canActivate: [AuthGuard],
 *   data: { roles: ['backoffice_admin'] }  // Optional role restriction
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  private router = inject(Router);
  private authService = inject(AuthService);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // Check if guards are disabled in environment (ONLY in development builds)
    const disableGuards = (environment as any).disableAuthGuards;
    if (disableGuards && !environment.production) {
      console.log('🔓 AuthGuard bypassed (development only)');
      return true;
    }
    
    // Production safety: ensure bypass is impossible in production
    if (disableGuards && environment.production) {
      console.error('⚠️ Security: disableAuthGuards ignored in production build');
    }

    // Wait for the startup silent-refresh to finish before checking auth.
    // Without this, a page refresh would always redirect to /login because
    // _accessToken is null until the BFF silent-refresh responds.
    return this.authService.initialized.then(async () => {
    // Check if user is authenticated
    const isAuthenticated = this.authService.isAuthenticated();

    if (!isAuthenticated) {
      console.warn('⚠️ User not authenticated, redirecting to login');
      // Redirect to login page with return url
      return this.router.createUrlTree(['/login'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // Verify user has valid roles
    return this.authService.verifyUserAccess().then(hasValidAccess => {
      if (!hasValidAccess) {
        console.warn('⚠️ [AUTHGUARD→/unauthorized] User has no valid roles');
        console.warn('  ➡ Route:', state.url);
        console.warn('  ➡ User object:', this.authService.getCurrentUser());
        return this.router.createUrlTree(['/unauthorized']);
      }

      // Check role-based access if roles are specified in route data
      const requiredRoles = route.data['roles'] as UserRole[];
      if (requiredRoles && requiredRoles.length > 0) {
        const hasRequiredRole = this.authService.hasAnyRole(requiredRoles);
        
        if (!hasRequiredRole) {
          console.warn('⚠️ [AUTHGUARD→/unauthorized] User does not have required role');
          console.warn('  ➡ Route:', state.url);
          console.warn('  ➡ Required roles:', requiredRoles);
          console.warn('  ➡ User roles:', this.authService.getUserRoles());
          return this.router.createUrlTree(['/unauthorized']);
        }
      }

      return true;
    });
    }); // initialized.then()
  }
}
